import { Component, effect, inject, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ReactiveTypedFormsModule } from '@rxweb/reactive-form-validators';
import { Observable } from 'rxjs';
import { ShieldZoneResponse, WafProfileMinimal } from '@bn/app-shield';
import { PlanType } from '@bn/app-shield/enums';
import { CDN_ROUTES } from '@bn/dashboard-core/constants';
import { AppRoutes } from '@bn/dashboard-core/enums';
import { ThemeService } from '@bn/dashboard-shared/services';
import { UserTheme } from '@bn/shared/entities/enums';
import { ContentService } from '@bn/shared/services';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { StepsComponent } from '@bn/web-ui/steps-component/steps.component';
import { StepItem } from '@bn/web-ui/steps-component/wizard.service';
import { AppShieldSetupPlatformComponent, AppShieldSetupStartComponent } from '../../components';
import { AppShieldSetupPackageComponent } from '../../components/app-shield-setup/app-shield-setup-package/app-shield-setup-package.component';
import { ShieldApiService } from '../../services';
import { AppShieldByPullZoneStore, ShieldApiStore } from '../../stores';

@Component({
  templateUrl: './app-shield-setup.page.html',
  imports: [
    AppShieldSetupPackageComponent,
    AppShieldSetupPlatformComponent,
    AppShieldSetupPlatformComponent,
    AppShieldSetupStartComponent,
    AppShieldSetupStartComponent,
    ReactiveTypedFormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    StepsComponent,
  ],
  providers: [AppShieldByPullZoneStore],
})
export class AppShieldSetupPage {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private readonly shieldApiService = inject(ShieldApiService);

  readonly #shieldApiStore = inject(ShieldApiStore);

  public readonly contentService = inject(ContentService);
  public readonly theme$: Observable<UserTheme>;
  selectedStepIndex = signal(1);
  public stepItems: StepItem[] = [
    {
      item: 'start',
      stepIndex: 1,
      label: 'Start',
      enabled: true,
      completed: false,
      valid: true,
    },
    {
      item: 'platform',
      stepIndex: 2,
      label: 'Choose platform',
      enabled: false,
      completed: false,
      valid: true,
    },
    {
      item: 'confirm',
      stepIndex: 3,
      label: 'Confirm',
      enabled: false,
      completed: false,
      valid: true,
    },
  ];

  private updateStepItems(): void {
    const hasPlatformSelection = Boolean(this.selectedShieldWafProfileId);

    this.stepItems = [
      {
        item: 'start',
        stepIndex: 1,
        label: 'Start',
        enabled: true,
        completed: this.selectedStepIndex() > 1,
        valid: true,
      },
      {
        item: 'platform',
        stepIndex: 2,
        label: 'Choose platform',
        enabled: this.selectedStepIndex() >= 2 || hasPlatformSelection,
        completed: this.selectedStepIndex() > 2,
        valid: true,
      },
      {
        item: 'confirm',
        stepIndex: 3,
        label: 'Confirm',
        enabled: this.selectedStepIndex() >= 3,
        completed: false,
        valid: true,
      },
    ];
  }

  public profiles!: WafProfileMinimal[];
  public selectedShieldWafProfileId: ShieldZoneResponse['wafProfileId'] = 0;
  public selectedShieldProfile!: WafProfileMinimal;
  public selectedShieldPackage: PlanType = PlanType.Advanced;

  constructor() {
    effect(() => {
      if (this.#shieldApiStore.setupRequired() === false && this.#shieldApiStore.selectedEntity()()) {
        this.router.navigate(['..', CDN_ROUTES.ShieldOverview], { relativeTo: this.route });
      }
    });

    this.contentService.setTitle('Configure Bunny Shield');
    this.theme$ = inject(ThemeService).getCurrentTheme();

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step: 1 },
      queryParamsHandling: 'merge',
    });

    this.route.queryParams.subscribe((params) => {
      this.selectedStepIndex.set(+params['step'] || this.selectedStepIndex());
      this.updateStepItems();
    });

    this.getProfiles();
  }

  public getProfiles() {
    this.shieldApiService.getShieldWafProfiles().subscribe({
      next: (profiles) => {
        this.profiles = profiles;
        this.setShieldProfile(profiles[0].id);
      },
      error: () => {
        console.error('Failed to fetch profiles');
      },
    });
  }

  public startSetup() {
    this.onStepClick(2);
  }

  public onStepClick(stepIndex: number): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step: stepIndex },
      queryParamsHandling: 'merge',
    });
  }

  public setShieldProfile(profileId: WafProfileMinimal['id']) {
    this.selectedShieldWafProfileId = profileId;

    const selectedProfile = this.profiles.find((profile) => profile.id === profileId);
    if (selectedProfile) {
      this.selectedShieldProfile = selectedProfile;
      this.updateStepItems();
    }
  }

  public setShieldPackage(profileId: PlanType) {
    this.selectedShieldPackage = profileId;
    this.updateStepItems();
  }

  public completeSetup() {
    this.#shieldApiStore.addShield({
      shieldZone: {
        planType: this.selectedShieldPackage,
        wafProfileId: this.selectedShieldWafProfileId,
      },
    });

    const pullZoneId = this.#shieldApiStore.pullZoneId();
    this.router.navigate([`../${AppRoutes.CDN}/${pullZoneId}/${AppRoutes.Shield}`]);
  }
}
